import { AxiosError } from 'axios';
import * as React from 'react';
import { generatePassword } from '../../../components_v2/utils';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { GreySidely3 } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { importWrapper } from '../../../utils/import';
import { AlertCall } from '../../alert/AlertProvider';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { FlexDiv } from '../../products/style';
import { downloadCsv, mapColumnsPopup, mappedRows, Target } from '../../settings/subSettings/importSettingsEntities';
import { getTeams, Team } from '../../teams/actions';
import { getGroups, getMembers, getUserById, postUser, putUser } from '../action';
import { Group, MemberRow } from '../model';

// Import / export of the account's users. The import pivots on the email:
// a known email updates the user (unmapped columns and, unless the file maps
// them, groups and teams stay untouched), an unknown one creates the user
// with a generated password, offered as a csv at the end. Creation and update
// go through the same cerberus calls as the user popup, row by row.

const TARGETS: Target[] = [
	{ key: 'email', label: () => translateToString('email'), candidates: ['email', 'e-mail', 'mail'], required: true },
	{ key: 'name', label: () => translateToString('name'), candidates: ['name', 'nom'] },
	{ key: 'phone', label: () => translateToString('phone'), candidates: ['phone', 'telephone'] },
	{ key: 'status', label: () => translateToString('admin.table.status'), candidates: ['status', 'statut', 'actif', 'active'] },
	{ key: 'groups', label: () => translateToString('admin.table.groups'), candidates: ['groups', 'groupes', 'groupes de securite'] },
	{ key: 'teams', label: () => translateToString('teams'), candidates: ['teams', 'equipes'] },
];

function parseStatus(raw: string | undefined): boolean | undefined {
	if (raw === undefined) return undefined;
	const lower = raw.trim().toLowerCase();
	if (['actif', 'active', 'oui', 'yes', 'true', '1'].includes(lower)) return true;
	if (['inactif', 'inactive', 'non', 'no', 'false', '0'].includes(lower)) return false;
	return undefined;
}

// comma-separated names to ids; unknown names land in warnings
function resolveNames(raw: string, byName: Map<string, number>, line: number, column: string, warnings: string[]): number[] {
	const ids: number[] = [];
	for (const name of raw.split(',').map(name => name.trim()).filter(name => name.length > 0)) {
		const id = byName.get(name.toLowerCase());
		if (id === undefined) warnings.push(translateToString('import.settings.warning_line', [['line', String(line)], ['column', column], ['value', name]]));
		else ids.push(id);
	}
	return ids;
}

export function importMembers(alert: AlertCall, setLoading: (loading: boolean) => void, onDone: () => void) {
	importWrapper<never>(async(data) => {
		if (!Array.isArray(data[0])) {
			launchToastError(translateToString('import.promotions.errors.bad_file'));
			return;
		}
		const mapping = await mapColumnsPopup(alert, data[0] as unknown[], TARGETS, 'import.settings.users_hint');
		if (!mapping) return;
		const { rows, lines } = mappedRows(data, mapping, TARGETS);
		if (rows.length === 0) {
			launchToastError(translateToString('import.promotions.errors.bad_file'));
			return;
		}

		// the rows go through cerberus one by one: the whole run hides behind
		// the report-style full-screen loader
		setLoading(true);
		const fetched = await Promise.all([
			getMembers(100000, 0).then(res => res.data as MemberRow[]),
			getGroups().then(res => res.data as Group[]),
			getTeams(),
		]).catch(e => {
			console.error(e);
			launchToastError(translateToString('import.products.failed'));
		});
		if (!fetched) {
			setLoading(false);
			return;
		}
		const [members, groups, teams] = fetched;
		const byEmail = new Map(members.filter(m => m.email).map(m => [m.email!.trim().toLowerCase(), m.id]));
		const groupsByName = new Map(groups.map(g => [g.name.trim().toLowerCase(), g.id]));
		const teamsByName = new Map(teams.map(t => [t.name.trim().toLowerCase(), t.id]));

		let created = 0;
		let updated = 0;
		const skipped: number[] = [];
		const warnings: string[] = [];
		const passwords: [string, string][] = [];
		// (line, name, email) of the rows cerberus refused with a 409: the
		// email already belongs to another sidely account
		const conflicts: [string, string, string][] = [];
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			const line = lines[i];
			const email = row.email?.trim();
			if (!email) {
				skipped.push(line);
				continue;
			}
			const status = parseStatus(row.status);
			if (row.status !== undefined && status === undefined) {
				warnings.push(translateToString('import.settings.warning_line', [['line', String(line)], ['column', TARGETS[3].label()], ['value', row.status]]));
			}
			const groupIds = row.groups === undefined ? undefined : resolveNames(row.groups, groupsByName, line, TARGETS[4].label(), warnings);
			const teamIds = row.teams === undefined ? undefined : resolveNames(row.teams, teamsByName, line, TARGETS[5].label(), warnings);
			try {
				const existingId = byEmail.get(email.toLowerCase());
				if (existingId !== undefined) {
					// unmapped columns never overwrite; groups and teams fall
					// back to what the user already has
					const details = await getUserById(existingId);
					await putUser(existingId, {
						user: { name: row.name, phone: row.phone, status },
						password: 'Unchanged',
						groups: groupIds ?? details?.groups ?? [],
						teams: teamIds ?? details?.teams ?? [],
					});
					updated += 1;
				} else {
					if (!row.name) {
						skipped.push(line);
						continue;
					}
					const password = generatePassword();
					const id = await postUser({
						is_owner: false,
						name: row.name,
						email,
						phone: row.phone,
						status: status ?? true,
						password: { New: password },
						groups: groupIds ?? [],
						teams: teamIds ?? [],
					});
					byEmail.set(email.toLowerCase(), id);
					passwords.push([email, password]);
					created += 1;
				}
			} catch (e) {
				skipped.push(line);
				if ((e as AxiosError).response?.status === 409) {
					conflicts.push([String(line), row.name ?? '', email]);
				} else {
					console.error(e);
					warnings.push(translateToString('import.settings.warning_line', [['line', String(line)], ['column', TARGETS[0].label()], ['value', email]]));
				}
			}
		}

		setLoading(false);
		onDone();
		alert({
			title: translateToString('import.products.summary_title'),
			width: 'min(520px, 95vw)',
			content: () => <FlexDiv flow='column' align='stretch' gap='10px'>
				<p style={{ margin: 0, fontSize: '13px' }}>
					{translateToString('import.settings.users_summary', [
						['created', String(created)],
						['updated', String(updated)],
						['skipped', String(skipped.length)],
					])}
				</p>
				{skipped.length > 0 && <p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}>
					{translateToString('import.settings.skipped_lines', [['lines', skipped.slice(0, 30).join(', ')]])}
				</p>}
				{warnings.slice(0, 15).map((warning, i) => <p key={i} style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}>{warning}</p>)}
				{conflicts.length > 0 && <>
					<p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}>
						{translateToString('import.settings.email_conflicts', [['count', String(conflicts.length)]])}
					</p>
					<FlexDiv justify='center'>
						<DefaultButton margin='5px 0 0 0' onClick={() => downloadCsv(
							'conflits_emails.csv',
							[translateToString('import.promotions.skipped.line'), translateToString('name'), translateToString('email')],
							conflicts,
						)}><Translate id='import.promotions.skipped.download' /></DefaultButton>
					</FlexDiv>
				</>}
				{passwords.length > 0 && <FlexDiv justify='center'>
					<DefaultButton margin='5px 0 0 0' onClick={() => downloadCsv(
						'passwords.csv',
						[translateToString('email'), translateToString('password')],
						passwords,
					)}><Translate id='import.settings.download_passwords' /></DefaultButton>
				</FlexDiv>}
			</FlexDiv>,
		});
	}, alert);
}

export function exportMembers(): void {
	Promise.all([getMembers(100000, 0).then(res => res.data as MemberRow[]), getTeams()]).then(([members, teams]: [MemberRow[], Team[]]) => {
		downloadCsv(
			'users.csv',
			[
				translateToString('name'),
				translateToString('email'),
				translateToString('phone'),
				translateToString('admin.table.status'),
				translateToString('admin.table.groups'),
				translateToString('teams'),
			],
			members.map(member => [
				member.name ?? '',
				member.email ?? '',
				member.phone ?? '',
				translateToString(member.status ? 'active' : 'inactive'),
				(member.group_info ?? []).map(([, name]) => name).join(', '),
				teams.filter(team => team.users.includes(member.id)).map(team => team.name).join(', '),
			]),
		);
	}).catch(console.error);
}
